import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { Toast, ToastDescription, ToastProvider, ToastViewport } from './toast';

interface ToastItem {
  id: number;
  message: string;
  /** ms; a refusal stays longer than a confirmation (1.4.1: at least 6 seconds). */
  duration?: number;
}

type ToastFn = (message: string, opts?: { duration?: number }) => void;
const Ctx = createContext<ToastFn | null>(null);

/** Toaster: shows short confirmations ("Diagnostic copied."). Every toast is also announced. */
export function Toaster({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const toast = useCallback<ToastFn>((message, opts) => {
    setItems((xs) => [...xs, { id: Date.now() + Math.random(), message, duration: opts?.duration }]);
  }, []);
  const value = useMemo(() => toast, [toast]);
  return (
    <Ctx.Provider value={value}>
      <ToastProvider duration={4000}>
        {children}
        {items.map((it) => (
          <Toast key={it.id} duration={it.duration} onOpenChange={(open) => !open && setItems((xs) => xs.filter((x) => x.id !== it.id))}>
            <ToastDescription>{it.message}</ToastDescription>
          </Toast>
        ))}
        <ToastViewport />
      </ToastProvider>
    </Ctx.Provider>
  );
}

export function useToast(): ToastFn {
  const v = useContext(Ctx);
  if (!v) throw new Error('useToast outside Toaster');
  return v;
}
